import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Trash2, X } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/connections";
import type { OneCBaseList } from "@/api/onec_bases";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { extractErrorMessage } from "@/lib/api-errors";
import { useOneCBases } from "@/lib/use-onec-bases";
import { SsoConnectionCard } from "@/pages/settings/SsoConnectionCard";
import type { OneCConnectionView } from "@/types/api";

type FormState = {
  name: string;
  base_url: string;
  username: string;
  password: string;
};

const EMPTY: FormState = { name: "", base_url: "", username: "", password: "" };

/** Подпись сопоставления подключения со справочником баз. Базы в ответе нет —
 *  рассинхрон: называем его, а не бросаем в рендере. */
function baseLabel(c: OneCConnectionView, bases: OneCBaseList | undefined): string | null {
  if (c.onec_base_id === null) return null;
  if (!bases) return "в справочнике";
  const base = bases.bases.find((b) => b.id === c.onec_base_id);
  return base ? `справочник: ${base.name}` : `⚠ база ${c.onec_base_id} не найдена в справочнике`;
}

const BADGE = "rounded px-1.5 py-0.5 text-xs";

export function Connection1CPage(): React.ReactElement {
  const qc = useQueryClient();
  // SSO-сессия: в 1С портал ходит по JWT в базу подключения, а она должна быть
  // в справочнике. Поэтому новое подключение — выбор базы из справочника, без
  // логина и пароля; подключение без базы справочника в SSO не работает.
  const { user } = useAuth();
  const sso = user?.auth_mode === "sso";
  const { data: bases } = useOneCBases();
  const { data: connections = [], isLoading } = useQuery({
    queryKey: ["connections"],
    queryFn: api.listConnections,
  });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: api.createConnection,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      setForm(EMPTY);
      toast.success("Подключение создано");
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e, "Ошибка создания")),
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: string; body: api.ConnectionUpdate }) =>
      api.updateConnection(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      setEditingId(null);
      setForm(EMPTY);
      toast.success("Подключение обновлено");
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e, "Ошибка обновления")),
  });

  const deleteMut = useMutation({
    mutationFn: (args: { id: string; force?: boolean }) =>
      api.deleteConnection(args.id, args.force ?? false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      toast.success("Подключение удалено");
    },
    onError: (e: { response?: { status?: number; data?: { detail?: string } } }, args) => {
      // Бэкенд отвечает 409 если есть зависимые маппинги «Договоры
      // комиссии». Подтверждаем и повторяем с force=true.
      if (e.response?.status === 409 && !args.force) {
        const msg = e.response?.data?.detail ?? "Есть зависимые маппинги. Удалить всё равно?";
        if (window.confirm(msg)) {
          deleteMut.mutate({ id: args.id, force: true });
        } else {
          // Явный feedback на отмену — иначе клик уходит в никуда.
          toast.message("Удаление отменено");
        }
        return;
      }
      toast.error(extractErrorMessage(e, "Ошибка удаления"));
    },
  });

  const testMut = useMutation({
    mutationFn: api.testConnection,
    onSuccess: (res) => {
      if (res.ok) toast.success(res.detail);
      else toast.error(res.detail);
    },
    // Брошенная ошибка (в SSO — 403 onec_not_configured и т.п.) — причину
    // сервера показываем, иначе клик уходит в никуда.
    onError: (e: unknown) =>
      toast.error(extractErrorMessage(e, "Не удалось проверить подключение")),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      const body: api.ConnectionUpdate = {
        name: form.name,
        base_url: form.base_url,
        username: form.username,
      };
      if (form.password) body.password = form.password;
      updateMut.mutate({ id: editingId, body });
    } else {
      createMut.mutate(form);
    }
  };

  const startEdit = (c: OneCConnectionView) => {
    setEditingId(c.id);
    setForm({
      name: c.name,
      base_url: c.base_url,
      username: c.username ?? "",
      password: "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Подключение к 1С УНФ</h1>
        {!sso && (
          <p className="text-sm text-muted-foreground">
            Введите базовый адрес 1С без постфиксов (например{" "}
            <code className="rounded bg-muted px-1">http://1c.example.com/unf</code>
            ). Постфиксы <code className="rounded bg-muted px-1">/odata/standard.odata</code> и{" "}
            <code className="rounded bg-muted px-1">/hs/api-requests</code> подставляются
            автоматически.
          </p>
        )}
      </div>

      {sso ? (
        <SsoConnectionCard />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {editingId ? "Редактирование подключения" : "Новое подключение"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="name">Название</Label>
                <Input
                  id="name"
                  placeholder="Основная база УНФ"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="base_url">Адрес базы (без постфиксов)</Label>
                <Input
                  id="base_url"
                  placeholder="http://1c.example.com/unf"
                  value={form.base_url}
                  onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  required
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="username">Логин 1С</Label>
                  <Input
                    id="username"
                    autoComplete="off"
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">
                    Пароль 1С{" "}
                    {editingId && (
                      <span className="text-xs">(оставьте пустым чтобы не менять)</span>
                    )}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required={!editingId}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createMut.isPending || updateMut.isPending}>
                  {editingId ? "Сохранить" : "Создать"}
                </Button>
                {editingId && (
                  <Button type="button" variant="ghost" onClick={cancelEdit}>
                    Отмена
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Мои подключения</h2>
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {!isLoading && connections.length === 0 && (
          <p className="text-sm text-muted-foreground">Подключений ещё нет.</p>
        )}
        <ul className="space-y-2">
          {connections.map((c) => (
            <li
              key={c.id}
              className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-medium">
                  {c.name}
                  {c.is_default && (
                    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                      по умолчанию
                    </span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {c.base_url} · {c.username ?? "без логина 1С"} ·{" "}
                  {c.has_password ? "пароль сохранён" : "без пароля"}
                </div>
                <ConnectionBaseBadge label={baseLabel(c, bases)} sso={sso} />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => testMut.mutate(c.id)}
                  disabled={testMut.isPending}
                >
                  <Check className="size-4" /> Проверить
                </Button>
                {/* Правка — логин/пароль/адрес, это парольное подключение. В SSO
                    подключение меняют пересозданием из справочника. */}
                {!sso && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Редактировать ${c.name}`}
                    onClick={() => startEdit(c)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (window.confirm(`Удалить подключение «${c.name}»?`)) {
                      deleteMut.mutate({ id: c.id });
                    }
                  }}
                >
                  <Trash2 className="size-4 text-destructive" />
                </Button>
                {!c.is_default && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => updateMut.mutate({ id: c.id, body: { is_default: true } })}
                  >
                    <X className="size-4" /> Сделать основным
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Сопоставлено ли подключение с базой справочника. В SSO-сессии подключение
 *  без базы справочника не работает — это предупреждение, а не справка. */
function ConnectionBaseBadge({
  label,
  sso,
}: {
  label: string | null;
  sso: boolean;
}): React.ReactElement {
  if (label !== null) {
    return (
      <div className={`${BADGE} mt-1 inline-block bg-muted text-muted-foreground`}>{label}</div>
    );
  }
  if (sso) {
    return (
      <div className={`${BADGE} mt-1 inline-block bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300`}>
        нет в справочнике баз — в SSO-сессии не работает
      </div>
    );
  }
  return (
    <div className={`${BADGE} mt-1 inline-block bg-muted text-muted-foreground`}>
      нет в справочнике баз
    </div>
  );
}
